import React, { useState } from "react";
import { ChevronDown, List } from "lucide-react";
import { ChatReplyOption, ChatReplyUi } from "@/types";

interface Props {
  ui: ChatReplyUi;
  disabled?: boolean;
  onSelect: (option: ChatReplyOption) => void;
}

/**
 * The buttons or list sent with a claims assistant reply, as WhatsApp shows them:
 * buttons as chips under the message, a list as a card that opens into its rows.
 */
const ChatReplyActions: React.FC<Props> = ({ ui, disabled, onSelect }) => {
  const [open, setOpen] = useState(false);

  if (ui.type === "buttons") {
    return (
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
        {ui.options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(option)}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-[#2a2a2a] tw-bg-white tw-border tw-border-[#00C5FB] tw-rounded-full tw-shadow-sm hover:tw-bg-[#00C5FB]/10 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="tw-mt-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-shadow-sm tw-overflow-hidden tw-w-full tw-max-w-sm">
      {(ui.title || ui.description) && (
        <div className="tw-px-4 tw-pt-3">
          {ui.title && <p className="tw-text-sm tw-font-semibold tw-text-[#2a2a2a]">{ui.title}</p>}
          {ui.description && <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{ui.description}</p>}
        </div>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-4 tw-py-3 tw-mt-2 tw-border-t tw-border-gray-100 tw-text-sm tw-font-medium tw-text-[#0096c7] hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        <List size={16} />
        {ui.button}
        <ChevronDown size={16} className={`tw-transition-transform ${open ? "tw-rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="tw-border-t tw-border-gray-100 tw-max-h-72 tw-overflow-y-auto">
          {ui.options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  setOpen(false);
                  onSelect(option);
                }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 hover:tw-bg-[#00C5FB]/10 tw-transition-colors disabled:tw-opacity-50"
              >
                <span className="tw-block tw-text-sm tw-text-[#2a2a2a]">{option.label}</span>
                {option.description && (
                  <span className="tw-block tw-text-xs tw-text-gray-500 tw-mt-0.5">{option.description}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ChatReplyActions;
